import { Button, ButtonVariant } from '@Core/Components'
import { FilterSelect } from '@/components/filter-select'
import { Loader } from '@Core/Components/Loader/Loader'
import { PermissionWrapper } from '@Core/Components/PermissionWrapper/PermissionWrapper'
import { usePermissions } from '@Core/Hooks/usePermissions'

const PermissionDeniedHint = ({
    permission,
    message,
    testId,
}: {
    permission: string
    message: string
    testId: string
}) => {
    const { hasPermission } = usePermissions()
    if (hasPermission(permission)) return null
    return (
        <p className="mt-2 text-xs italic text-gray-500 dark:text-gray-400" data-testid={testId}>
            {message}
        </p>
    )
}
import {
    SupplierQuote,
    SupplierQuoteStatus,
} from '@Purchasing/Domain/Model/PurchaseProcess'
import { useSupplierQuoteActions } from '@Purchasing/Hooks/useSupplierQuoteActions/useSupplierQuoteActions'
import { useSupplierQuotes } from '@Purchasing/Hooks/useSupplierQuotes/useSupplierQuotes'
import { roles } from '@Purchasing/roles'
import { formatBRL } from '@Purchasing/Utils/money'
import { useMemo, useState } from 'react'
import { NewSupplierQuoteModal } from './NewSupplierQuoteModal'
import { QuoteTable } from './QuoteTable'
import { SelectQuoteModal, supplierNameOf } from './SelectQuoteModal'

type SortKey = 'none' | 'total' | 'leadTime'

interface PurchaseProcessQuotesProps {
    processId: string
    selectedSupplierQuoteId?: string | null
    suggestedQuantity?: number
    reloadProcess: () => void
    /** Parent re-lê as cotações (hero "Cotação vencedora" etc.). */
    onQuotesReload?: () => void
    /** Processo encerrado: cotações ficam só para consulta. */
    closed?: boolean
    /** Compra ativa: a vencedora não pode ser trocada. */
    winnerLocked?: boolean
}

export const PurchaseProcessQuotes = ({
    processId,
    selectedSupplierQuoteId,
    suggestedQuantity,
    reloadProcess,
    onQuotesReload,
    closed = false,
    winnerLocked = false,
}: PurchaseProcessQuotesProps) => {
    const { quotes, loading, reload } = useSupplierQuotes(processId)
    const {
        createQuote,
        updateQuote,
        changeStatus,
        selectWinner,
        removeQuote,
        loading: actionLoading,
        error: actionError,
    } = useSupplierQuoteActions()
    const [isNewQuoteOpen, setIsNewQuoteOpen] = useState(false)
    const [quoteToReceive, setQuoteToReceive] = useState<SupplierQuote | null>(null)
    const [quoteToSelect, setQuoteToSelect] = useState<SupplierQuote | null>(
        null
    )
    const [sortKey, setSortKey] = useState<SortKey>('none')

    const activeQuotes = useMemo(
        () => quotes.filter((q) => q.active !== false),
        [quotes]
    )

    const currentWinner = useMemo(
        () =>
            activeQuotes.find((q) => q._id === selectedSupplierQuoteId) ??
            null,
        [activeQuotes, selectedSupplierQuoteId]
    )

    const sortedQuotes = useMemo(() => {
        if (sortKey === 'total') {
            return [...activeQuotes].sort(
                (a, b) => (a.totalCents ?? 0) - (b.totalCents ?? 0)
            )
        }
        if (sortKey === 'leadTime') {
            return [...activeQuotes].sort((a, b) => {
                const av = a.leadTimeDays ?? Number.MAX_SAFE_INTEGER
                const bv = b.leadTimeDays ?? Number.MAX_SAFE_INTEGER
                return av - bv
            })
        }
        return activeQuotes
    }, [activeQuotes, sortKey])

    const summary = useMemo(() => {
        const suppliers = new Set(activeQuotes.map((q) => q.supplierId))
        return {
            suppliersCount: suppliers.size,
            receivedCount: activeQuotes.filter(
                (q) => q.status === 'RECEIVED'
            ).length,
            requestedCount: activeQuotes.filter(
                (q) => q.status === 'REQUESTED'
            ).length,
            rejectedCount: activeQuotes.filter(
                (q) => q.status === 'REJECTED'
            ).length,
        }
    }, [activeQuotes])

    const handleConfirmSelect = async (quote: SupplierQuote) => {
        const result = await selectWinner(processId, quote._id)
        setQuoteToSelect(null)
        if (result) {
            await reload()
            reloadProcess()
            onQuotesReload?.()
        }
    }

    const handleCreateQuote = async (payload: any) => {
        const result = await createQuote(processId, payload)
        if (result) {
            await reload()
            onQuotesReload?.()
        }
        return result
    }

    // Solicitada → Recebida: grava os valores enviados pelo fornecedor e só
    // então muda o status (o back só deixa selecionar cotação RECEIVED).
    const handleReceiveQuote = async (quote: SupplierQuote, payload: any) => {
        const updated = await updateQuote(processId, quote._id, payload)
        if (!updated) return null
        const result = await changeStatus(processId, quote._id, 'RECEIVED' as SupplierQuoteStatus)
        await reload()
        onQuotesReload?.()
        return result
    }

    const handleReject = async (quote: SupplierQuote) => {
        const result = await changeStatus(
            processId,
            quote._id,
            'REJECTED' as SupplierQuoteStatus
        )
        if (result) {
            await reload()
            onQuotesReload?.()
        }
    }

    const handleCancel = async (quote: SupplierQuote) => {
        const result = await changeStatus(
            processId,
            quote._id,
            'CANCELED' as SupplierQuoteStatus
        )
        if (result) {
            await reload()
            onQuotesReload?.()
        }
    }

    const handleRemove = async (quote: SupplierQuote) => {
        const result = await removeQuote(processId, quote._id)
        if (result !== null) {
            await reload()
            onQuotesReload?.()
        }
    }

    return (
        <section className="mt-6 rounded-card border border-border p-4 md:p-6" data-testid="quotes-section">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-display text-lg font-bold">Cotações</h2>
                <div className="flex flex-wrap items-center gap-3">
                    {/* §8 — sem cotações, o sort não tem função; esconder */}
                    {sortedQuotes.length > 0 && (
                        <FilterSelect
                            label="Ordenar comparação"
                            value={sortKey}
                            onChange={(value) => setSortKey(value as SortKey)}
                            options={[
                                { value: 'none', label: 'Ordem de cadastro' },
                                { value: 'total', label: 'Menor total' },
                                { value: 'leadTime', label: 'Menor prazo' },
                            ]}
                        />
                    )}
                    {!closed && (
                        <PermissionWrapper
                            permission={roles.quote.permissions.create.key}
                        >
                            <Button
                                variant={ButtonVariant.primary}
                                onClick={() => setIsNewQuoteOpen(true)}
                                data-testid="new-quote-header"
                            >
                                Nova Cotação
                            </Button>
                        </PermissionWrapper>
                    )}
                </div>
            </div>

            <p className="text-sm text-gray-500 mb-3" data-testid="quotes-summary">
                {summary.suppliersCount} fornecedor(es) consultado(s) ·{' '}
                {summary.requestedCount} solicitada(s) · {summary.receivedCount}{' '}
                recebida(s) · {summary.rejectedCount} rejeitada(s)
                {currentWinner && (
                    <>
                        {' '}
                        · <strong>Selecionada:</strong>{' '}
                        {supplierNameOf(currentWinner)} —{' '}
                        {formatBRL(currentWinner.totalCents)}
                    </>
                )}
            </p>

            {(closed || winnerLocked) && (
                <p className="mb-3 rounded-r-md border-l-[3px] border-l-brand bg-accent px-4 py-2 text-sm text-muted-foreground" data-testid="quotes-locked-hint">
                    {closed
                        ? 'Processo encerrado: as cotações ficam disponíveis só para consulta.'
                        : 'A compra já foi gerada a partir da cotação vencedora. Para trocar a vencedora, cancele a compra primeiro.'}
                </p>
            )}

            {actionError && (
                <p className="text-od-secondary-600 text-sm mb-2">{actionError}</p>
            )}

            {loading ? (
                <Loader />
            ) : sortedQuotes.length === 0 ? (
                <div
                    className="rounded-md border border-dashed border-gray-300 p-6 text-center dark:border-gray-600"
                    data-testid="quotes-empty"
                >
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Nenhuma cotação registrada ainda.
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Registre propostas de fornecedores para comparar preço,
                        prazo e condições.
                    </p>
                    <div className="mt-3">
                        <PermissionWrapper
                            permission={roles.quote.permissions.create.key}
                        >
                            <Button
                                variant={ButtonVariant.primary}
                                onClick={() => setIsNewQuoteOpen(true)}
                                data-testid="new-quote-empty"
                            >
                                + Nova cotação
                            </Button>
                        </PermissionWrapper>
                        <PermissionDeniedHint
                            permission={roles.quote.permissions.create.key}
                            message="Você não possui permissão para registrar cotações."
                            testId="quotes-readonly-hint"
                        />
                    </div>
                </div>
            ) : (
                <QuoteTable
                    quotes={sortedQuotes}
                    selectedSupplierQuoteId={selectedSupplierQuoteId}
                    suggestedQuantity={suggestedQuantity}
                    readOnly={closed}
                    winnerLocked={winnerLocked}
                    onSelect={setQuoteToSelect}
                    onReceive={setQuoteToReceive}
                    onReject={handleReject}
                    onCancel={handleCancel}
                    onRemove={handleRemove}
                />
            )}

            <NewSupplierQuoteModal
                isOpen={isNewQuoteOpen || Boolean(quoteToReceive)}
                onClose={() => { setIsNewQuoteOpen(false); setQuoteToReceive(null) }}
                receivingQuote={quoteToReceive}
                onReceive={handleReceiveQuote}
                processId={processId}
                suggestedQuantity={suggestedQuantity}
                onCreate={handleCreateQuote}
            />
            <SelectQuoteModal
                isOpen={Boolean(quoteToSelect)}
                onClose={() => setQuoteToSelect(null)}
                quote={quoteToSelect}
                currentWinner={currentWinner}
                onConfirm={handleConfirmSelect}
                loading={actionLoading}
            />
        </section>
    )
}
